import { useCallback } from 'react'

import { useMutation } from '@fv/client-core'
import {
  type PermissionCategory,
  permissionsByCategory,
} from '@fv/client-types'
import {
  type ProductType,
  productTypes,
  type UserPermissionType,
  type VerifyTokenDto,
  type VerifyTokenResponse,
} from '@fv/models'

import {
  useAppContext,
  useSubscriptionStatus,
  useUserSettings,
} from '@/hooks/settings'
import { isCarrierAccountUser } from '@/utils/accountFuncs'
import { shipperFetch } from '@/utils/shipperFetch'

export function useAccountFeatures(): Record<ProductType, boolean> {
  const settingsQuery = useUserSettings()
  const subscriptionStatus = useSubscriptionStatus()
  const features = settingsQuery.data?.products
  const isTrial = subscriptionStatus === 'trial'
  const hasTruckload = !!features?.includes('truckLoad')

  const base = Object.fromEntries(
    productTypes.map(type => [type, !!features?.includes(type)]),
  ) as Record<ProductType, boolean>
  return {
    ...base,
    fuelAndDistance: isTrial || (base.fuelAndDistance && base.truckLoad),
    parcel: isTrial || base.parcel,
    truckLoad: isTrial || hasTruckload,
  }
}

const useContextPermissions = () => {
  const contextQuery = useAppContext()
  return contextQuery.data?.user.permissions
}

export const useHasPermissionFn = () => {
  const permissions = useContextPermissions()

  return useCallback(
    (permission: UserPermissionType) => !!permissions?.includes(permission),
    [permissions],
  )
}
export const useHasPermission = (permission: UserPermissionType) => {
  const permissions = useContextPermissions()
  return !!permissions?.includes(permission)
}
export const useHasAnyPermission = () => {
  const permissions = useContextPermissions()

  return useCallback(
    (names: UserPermissionType[]) =>
      !!permissions?.some(name => names.includes(name)),
    [permissions],
  )
}

export const useHasPermissionInLevel = (level: PermissionCategory) => {
  const permissions = permissionsByCategory[level]
  const hasPermission = useHasPermissionFn()

  return permissions.some(perm => hasPermission(perm))
}

export const useIsViewOnly = () => {
  const permissions = useContextPermissions()
  return !permissions?.length
}

export function usePermissions() {
  const hasPermission = useHasPermissionFn()
  return {
    canAudit: hasPermission('audit-viewer'),
    canBook: hasPermission('load-booker'),
    canQuote: hasPermission('load-creator'),
    canManageProducts: hasPermission('manage-products'),
    canViewRates: hasPermission('view-rates'),
  }
}

export const useIsCarrierAccount = () => {
  const context = useAppContext()
  if (!context.data?.user) return false

  return isCarrierAccountUser(context.data.user)
}

export const useVerifyToken = () => {
  return useMutation((body: VerifyTokenDto) =>
    shipperFetch<VerifyTokenResponse>('/auth/verify', {
      body,
      method: 'POST',
    }),
  )
}

export const useIsAdminAccount = () => {
  const context = useAppContext()
  if (!context.data?.user) return false

  return context.data.user.accountRoles?.includes('admins') ?? false
}

export const useRole = () => {
  const contextQuery = useAppContext()
  if (!contextQuery.data.user) throw new Error('User data not present')

  return contextQuery.data.user.userRole
}
